/**
 * Wikidata client - linking an uploaded Commons file to a Wikidata item
 * (T434087).
 *
 * A separate module from commons-api.ts because Wikidata is a separate wiki.
 * It needs its own CSRF token - tokens are per-wiki, so the commonswiki token
 * the upload path holds is rejected here - and nothing in this file is on
 * the upload path: every write happens after the file is published, and
 * callers must treat any failure as a warning, never as a failed upload.
 *
 * Two request styles, because MediaWiki only sends CORS headers when a request
 * asks for them, and anonymous and authenticated requests ask differently
 * (ApiMain::handleCORS):
 *
 *  - Item SEARCH is a public read, sent anonymously with `origin=*`. Without it
 *    the response arrives with status 200 and real data, and the browser
 *    discards it - which is what made the picker dead when signed out.
 *  - The token fetch, the existing-claims check and the WRITE carry the OAuth
 *    bearer header and `crossorigin`, the documented way to make an OAuth
 *    request both cross-origin and logged in. Without it MediaWiki sends no
 *    CORS headers on a successful response and every write fails in the
 *    browser. The Commons client uses the same parameter for the same reason.
 *
 * The OAuth consumer is a global one registered on Meta, so its edit grant
 * applies to Wikidata. If it ever does not, the write fails as `permission`.
 */

import {
  WIKIDATA_API_URL, WIKIDATA_ENTITY_BASE_URL, WIKIDATA_PROP_VIDEO, WIKIDATA_PROP_AUDIO
} from "./config"
import { getAccessToken, refreshAccessToken } from "./oauth"
import type { MediaMode, WikidataItem } from "./types"

// AUTH_BASE asks for authenticated CORS (`crossorigin`). MediaWiki answers a
// request carrying it with 403 when there is no OAuth session, which is
// correct here: these calls only run after a signed-in upload.
// ANON_BASE asks for anonymous CORS (`origin=*`) for the public search.
// The two parameters cannot be combined - MediaWiki rejects that outright.
//
// No `maxlag`, unlike the Commons client. On Wikidata, "lag" includes the
// Query Service's update lag, which routinely sits far above 5 seconds for
// long stretches - observed in production at 35 s - and while it does, every
// maxlag=5 request is refused. That left the picker showing no results and
// would have failed every link. maxlag is for bots; MediaWiki's guidance is
// that interactive requests, with a person waiting on the result, omit it.
// A maxlag error is still mapped cleanly should one ever come back.
const AUTH_BASE = WIKIDATA_API_URL + "?crossorigin=&format=json"
const ANON_BASE = WIKIDATA_API_URL + "?origin=*&format=json"

/**
 * No request in this module may hang indefinitely. The write runs before the
 * upload screen switches to "done", so an unanswered request would leave an
 * already-published file looking stuck, and browsers apply no fetch timeout
 * of their own. Covers the body as well as the headers.
 */
const REQUEST_TIMEOUT_MS = 20_000

/** Wikidata item ids: "Q" and a number with no leading zero. */
const ITEM_ID = /^Q[1-9]\d*$/

type WikidataErrorCode =
  | "permission"
  | "maxlag"
  | "api"
  | "http"
  | "network"
  | "timeout"
  | "malformed"

export class WikidataError extends Error {
  code: WikidataErrorCode
  constructor(code: WikidataErrorCode, message: string) {
    super(message)
    this.name = "WikidataError"
    this.code = code
  }
}

/** MediaWiki error codes that mean "you are not allowed to do this here",
 *  which for this tool almost always means the OAuth grant does not cover
 *  Wikidata. Worth its own code: retrying will not help. */
const PERMISSION_CODES = new Set([
  "permissiondenied", "mwoauth-invalid-authorization", "readapidenied",
  "writeapidenied", "protectedpage", "blocked", "autoblocked", "cascadeprotected"
])

function isAbort(err: unknown): boolean {
  // By name rather than instanceof DOMException: the class is not guaranteed
  // to be the same object across realms (jsdom, workers).
  return !!err && typeof err === "object" && (err as { name?: unknown }).name === "AbortError"
}

/* ------------------------------ Transport ------------------------------- */

interface ApiReply {
  ok: boolean
  status: number
  /** Parsed body, or null when it was not a JSON object - which is exactly
   *  what a rate-limited reply is: HTTP 429 answers with plain text. */
  json: Record<string, unknown> | null
}

/**
 * The single place any request in this module reaches the network.
 *
 * Never lets a non-JSON body throw, never waits longer than the deadline,
 * and turns transport failures into WikidataError. A caller's own abort is
 * the one thing passed through untouched, as an AbortError, because it is a
 * cancellation rather than a fault.
 */
async function requestJson(
  url: string, init: RequestInit = {}, signal?: AbortSignal
): Promise<ApiReply> {
  const controller = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => { timedOut = true; controller.abort() }, REQUEST_TIMEOUT_MS)
  const forwardAbort = () => controller.abort()
  if (signal?.aborted) controller.abort()
  else signal?.addEventListener("abort", forwardAbort, { once: true })

  try {
    const resp = await fetch(url, { ...init, signal: controller.signal })
    let json: Record<string, unknown> | null = null
    try {
      const parsed: unknown = await resp.json()
      if (parsed && typeof parsed === "object") json = parsed as Record<string, unknown>
    } catch (err) {
      if (timedOut || isAbort(err)) throw err
      // Not JSON. Left as null; the status says what went wrong.
    }
    return { ok: resp.ok, status: resp.status, json }
  } catch (err) {
    if (timedOut) throw new WikidataError("timeout", "Wikidata took too long to answer.")
    if (isAbort(err)) throw err
    throw new WikidataError(
      "network",
      err instanceof Error && err.message ? err.message : "Could not reach Wikidata.")
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener("abort", forwardAbort)
  }
}

/**
 * requestJson with the user's OAuth bearer token, retrying once with a
 * refreshed token on 401.
 *
 * This deliberately does not share the Commons client's authFetch. That one
 * is private to commons-api.ts, on the upload path, and sharing it would mean
 * editing the module every upload depends on to serve a post-upload extra.
 * The two have also diverged on purpose: this one has a deadline and never
 * throws on a non-JSON body. The part they do have in common - attach the
 * token, refresh once on 401 - is the few lines below. If a third caller
 * ever needs it, moving it into oauth.ts is the right consolidation.
 */
async function authRequestJson(url: string, init: RequestInit = {}): Promise<ApiReply> {
  const withToken = (token: string | null): RequestInit => token
    ? { ...init, headers: { ...(init.headers as Record<string, string> | undefined), Authorization: "Bearer " + token } }
    : init

  let reply = await requestJson(url, withToken(getAccessToken()))
  if (reply.status === 401) {
    const fresh = await refreshAccessToken()
    if (fresh) reply = await requestJson(url, withToken(fresh))
  }
  return reply
}

/** What went wrong with a reply, or null if nothing did. */
function problemWith(reply: ApiReply, what: string): WikidataError | null {
  const err = reply.json?.error as { code?: unknown; info?: unknown } | undefined
  if (err) {
    const code = String(err.code ?? "")
    const info = String(err.info || code || "Wikidata returned an error.")
    if (code === "maxlag") return new WikidataError("maxlag", info)
    if (PERMISSION_CODES.has(code)) return new WikidataError("permission", info)
    return new WikidataError("api", info)
  }
  if (reply.status === 429) {
    return new WikidataError("http", what + " was rate-limited by Wikidata (HTTP 429).")
  }
  if (!reply.ok) return new WikidataError("http", what + " failed: HTTP " + reply.status)
  if (!reply.json) {
    return new WikidataError("malformed", what + " returned a response we could not read.")
  }
  return null
}

/** Link to an item's page. */
export function wikidataEntityUrl(qid: string): string {
  return WIKIDATA_ENTITY_BASE_URL + encodeURIComponent(qid)
}

/* ------------------------------ Search ---------------------------------- */

const SEARCH_LIMIT = 7

/**
 * Wikidata labels are at most 250 characters, so a longer query cannot match
 * anything - and a long percent-encoded one (nine bytes per Devanagari
 * character) risks the CDN refusing the URL.
 */
const MAX_QUERY_LENGTH = 250

/** A pasted item URL: /wiki/Q5, /entity/Q5, Special:EntityPage/Q5, mobile. */
const ITEM_URL =
  /^https?:\/\/(?:www\.|m\.)?wikidata\.org\/(?:wiki\/(?:Special:EntityPage\/)?|entity\/)(Q\d+)(?:[/?#].*)?$/i

/**
 * The query as it will be sent: well-formed, trimmed, whitespace collapsed,
 * and capped. A lone UTF-16 surrogate (from a damaged paste) would make
 * encodeURIComponent throw, so any are dropped here.
 */
function cleanQuery(query: string): string {
  const wellFormed = String(query ?? "").replace(/\p{Cs}/gu, "")
  const collapsed = wellFormed.replace(/\s+/g, " ").trim()
  return Array.from(collapsed).slice(0, MAX_QUERY_LENGTH).join("").trim()
}

/** Strip leading zeros from a run of digits, keeping at least one. */
function canonicalDigits(digits: string): string {
  return digits.replace(/^0+(?=\d)/, "")
}

/**
 * An explicit item reference - "Q5", "q5", "Q05", or a pasted Wikidata item
 * URL - as its canonical id. Null for anything else, including a bare number,
 * which is ambiguous ("1984" is also a novel) and searched both ways.
 */
function parseItemId(query: string): string | null {
  const match = /^q(\d+)$/i.exec(query) || ITEM_URL.exec(query)
  if (!match) return null
  const id = "Q" + canonicalDigits(match[1].replace(/^q/i, ""))
  return ITEM_ID.test(id) ? id : null
}

/**
 * Whether a query is worth sending. The single source of truth: the picker
 * imports this rather than keeping its own copy, because when the two
 * disagreed, single-digit Q-id lookups were unreachable from the UI.
 *
 * Two characters normally; one when it is a digit, since a bare number is a
 * deliberate id lookup. Never for empty input, whitespace, or punctuation
 * alone - those cannot match an item and would only spend a request.
 */
export function isSearchable(query: string): boolean {
  const q = cleanQuery(query)
  if (!/[\p{L}\p{N}]/u.test(q)) return false
  return Array.from(q).length >= 2 || /^\d$/.test(q)
}

function searchUrl(term: string, lang: string): string {
  return ANON_BASE +
    "&action=wbsearchentities&type=item&limit=" + SEARCH_LIMIT +
    "&search=" + encodeURIComponent(term) +
    // uselang alongside language so labels come back in the user's language
    // where one exists, and fall back to English where it does not.
    "&language=" + lang + "&uselang=" + lang
}

function toItems(json: Record<string, unknown> | null): WikidataItem[] {
  const results = json?.search
  if (!Array.isArray(results)) return []
  return results
    .filter((r: { id?: unknown }) => !!r && typeof r.id === "string" && ITEM_ID.test(r.id))
    .map((r: { id: string; label?: unknown; description?: unknown }) => ({
      qid: r.id,
      label: typeof r.label === "string" && r.label ? r.label : r.id,
      description: typeof r.description === "string" ? r.description : ""
    }))
}

/**
 * One search request. Never rejects: a failure resolves to null, so the two
 * halves of a bare-number lookup degrade independently - a 429 on one still
 * leaves the other's results - and a cancellation is not reported as one.
 */
async function searchOnce(
  term: string, lang: string, signal?: AbortSignal
): Promise<WikidataItem[] | null> {
  try {
    // Anonymous on purpose: see the module header.
    const reply = await requestJson(searchUrl(term, lang), {}, signal)
    const problem = problemWith(reply, "Wikidata search")
    if (problem) {
      console.warn("[c2c] Wikidata search failed:", problem.message)
      return null
    }
    return toItems(reply.json)
  } catch (err) {
    if (!isAbort(err)) console.warn("[c2c] Wikidata search failed:", err)
    return null
  }
}

/** Move an exact id match to the front, leaving everything else in order. */
function hoistExactId(items: WikidataItem[], qid: string): WikidataItem[] {
  const idx = items.findIndex(i => i.qid === qid)
  if (idx <= 0) return items
  return [items[idx], ...items.slice(0, idx), ...items.slice(idx + 1)]
}

/**
 * Search Wikidata items by label, by Q-id, or by a pasted item URL.
 *
 * Contract: never rejects. A query that is not worth sending, a failure, a
 * rate limit or a cancellation all yield an empty list. A picker that cannot
 * reach the network should go quiet, not put an error in front of someone
 * mid-form.
 */
export async function searchWikidataItems(
  query: string,
  language = "en",
  signal?: AbortSignal
): Promise<WikidataItem[]> {
  if (!isSearchable(query)) return []
  const q = cleanQuery(query)
  const lang = encodeURIComponent(language || "en")

  const explicitId = parseItemId(q)
  if (explicitId) {
    return hoistExactId((await searchOnce(explicitId, lang, signal)) ?? [], explicitId)
  }

  if (!/^\d+$/.test(q)) {
    return (await searchOnce(q, lang, signal)) ?? []
  }

  // A bare number. Verified live: plain search for "5" returns serotonin,
  // "42" a film, "9141" a number article - never the item with that id - so
  // the Q-form is looked up alongside and merged in front. Only all-digit
  // queries cost this second request, and the picker aborts both halves the
  // moment the user types on, so they cannot pile up against the rate limit.
  const wanted = "Q" + canonicalDigits(q)
  const [byId, byText] = await Promise.all([
    ITEM_ID.test(wanted) ? searchOnce(wanted, lang, signal) : Promise.resolve([]),
    searchOnce(q, lang, signal)
  ])
  const exact = (byId ?? []).filter(i => i.qid === wanted)
  const seen = new Set(exact.map(i => i.qid))
  return [...exact, ...(byText ?? []).filter(i => !seen.has(i.qid))].slice(0, SEARCH_LIMIT)
}

/* ------------------------------ Tokens ---------------------------------- */

/**
 * Fetch a CSRF token for Wikidata. Tokens are per-wiki, so the commonswiki
 * token the upload path holds cannot be reused here.
 */
export async function getWikidataCsrfToken(): Promise<string> {
  const reply = await authRequestJson(AUTH_BASE + "&action=query&meta=tokens&type=csrf")
  const problem = problemWith(reply, "Fetching a Wikidata token")
  if (problem) throw problem
  const tokens = (reply.json?.query as { tokens?: { csrftoken?: unknown } } | undefined)?.tokens
  const token = tokens?.csrftoken
  if (typeof token !== "string" || !token) {
    throw new WikidataError("malformed", "Wikidata did not return an edit token.")
  }
  if (token === "+\\") {
    // The anonymous token: the request reached Wikidata without being
    // recognised as this user - normally an OAuth grant that stops at Commons.
    throw new WikidataError(
      "permission",
      "This tool is not authorised to edit Wikidata. Its OAuth grant may not cover Wikidata.")
  }
  return token
}

/* ------------------------------ Statements ------------------------------ */

/**
 * Which property an upload belongs in. An audio-only upload publishes an Ogg
 * audio file, so asserting it as P10 (video) would put wrong data on the
 * item. Callers pass the media mode at write time, so a mode changed after
 * the item was picked is still honoured.
 */
export function propertyForMediaMode(mode: MediaMode | undefined): string {
  return mode === "audio-only" ? WIKIDATA_PROP_AUDIO : WIKIDATA_PROP_VIDEO
}

/** Upper-case the first character only, as Commons titles are. Leaves it
 *  alone where upper-casing would change its length (e.g. "ß" -> "SS"). */
function ucfirst(s: string): string {
  const [first, ...rest] = Array.from(s)
  if (first === undefined) return ""
  const upper = first.toUpperCase()
  return (Array.from(upper).length === 1 ? upper : first) + rest.join("")
}

/**
 * The canonical commonsMedia value: the bare title, as Wikidata stores it.
 *
 * Verified with wbparsevalue: Wikibase saves whatever form it is given. The
 * upload API hands back titles with underscores, and those - like a
 * lower-case first letter or doubled spaces - would be accepted and stored
 * verbatim as non-canonical values. A "File:" prefix is rejected outright.
 * So: no namespace prefix, underscores and whitespace runs as single spaces,
 * first letter upper-cased. The rest of the title is left exactly as it is,
 * because case after the first letter is significant - "Taj mahal.jpeg" and
 * "Taj Mahal.jpeg" are different files.
 */
export function normalizeMediaValue(filename: string): string {
  const bare = String(filename ?? "")
    .trim()
    .replace(/^(?:file|image)\s*:\s*/i, "")
    .replace(/[_\s]+/g, " ")
    .trim()
  return ucfirst(bare)
}

/**
 * Whether the item already carries this exact file under this property.
 *
 * Wikidata permits several values for P10, so a duplicate would be accepted
 * silently. Both sides are compared in canonical form, which is
 * case-sensitive after the first letter: matching case-insensitively would
 * treat a different file as "already present" and skip a write the user
 * asked for.
 *
 * A failed check answers false. Worst case we attempt a write the caller
 * reports on, which beats silently skipping a statement because a read
 * failed.
 */
export async function hasMediaStatement(
  qid: string,
  property: string,
  filename: string
): Promise<boolean> {
  const wanted = normalizeMediaValue(filename)
  if (!wanted || !ITEM_ID.test(qid)) return false
  try {
    const reply = await authRequestJson(
      AUTH_BASE + "&action=wbgetclaims&entity=" + encodeURIComponent(qid) +
      "&property=" + encodeURIComponent(property))
    if (problemWith(reply, "Checking existing statements")) return false
    const claims = (reply.json?.claims as Record<string, unknown> | undefined)?.[property]
    if (!Array.isArray(claims)) return false
    return claims.some((c: { mainsnak?: { datavalue?: { value?: unknown } } }) => {
      const value = c?.mainsnak?.datavalue?.value
      return typeof value === "string" && normalizeMediaValue(value) === wanted
    })
  } catch {
    return false
  }
}

export interface LinkResult {
  qid: string
  property: string
  filename: string
  /** True when the statement was already present and nothing was written. */
  alreadyPresent: boolean
}

/**
 * Add the uploaded file to a Wikidata item as a P10 / P51 statement.
 *
 * Rejects with a WikidataError on any failure. Callers must treat that as a
 * warning, never as an upload failure: by the time this runs the file is
 * published on Commons, and the contributor can add the statement by hand.
 */
export async function linkFileToWikidataItem(opts: {
  qid: string
  filename: string
  mediaMode?: MediaMode
}): Promise<LinkResult> {
  const qid = String(opts.qid ?? "").trim()
  if (!ITEM_ID.test(qid)) {
    throw new WikidataError("malformed", "Not a Wikidata item id: " + (opts.qid || "(empty)"))
  }
  const filename = normalizeMediaValue(opts.filename)
  if (!filename) {
    throw new WikidataError("malformed", "No file name to link.")
  }
  const property = propertyForMediaMode(opts.mediaMode)

  if (await hasMediaStatement(qid, property, filename)) {
    return { qid, property, filename, alreadyPresent: true }
  }

  const token = await getWikidataCsrfToken()

  const body = new FormData()
  body.append("action", "wbcreateclaim")
  body.append("entity", qid)
  body.append("snaktype", "value")
  body.append("property", property)
  // commonsMedia takes a JSON string value.
  body.append("value", JSON.stringify(filename))
  body.append("token", token)
  body.append("summary", "Linking a file uploaded with clip2commons")

  const reply = await authRequestJson(AUTH_BASE, { method: "POST", body })
  const problem = problemWith(reply, "Adding the Wikidata statement")
  if (problem) throw problem
  if (reply.json?.success !== 1) {
    throw new WikidataError("api", "Wikidata did not save the statement.")
  }
  return { qid, property, filename, alreadyPresent: false }
}
